import { useState } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Tabs,
  TabsContent,
} from '@monksflow/monks-ui';
import { BookOpenIcon, FileUpIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { PersonaDto } from '@/lib/api/personas-client';
import { UnderlineTabsList, UnderlineTabsTrigger } from '@/shared/ui';
import {
  isExtractionInFlight,
  useAnalysisFormStore,
  type AnalysisFormState,
  type AudienceSource,
} from '../../../model/analysisFormStore';
import { useAnalysisStepValidity } from '../../../model/useAnalysisStepValidity';
import { ModeChoiceStep } from '../shared';
import { KnowledgeHubAudiencePicker } from './KnowledgeHubAudiencePicker';
import { UploadDocumentTab } from './UploadDocumentTab';

/* eslint-disable i18next/no-literal-string -- tab identifier values */
const KNOWLEDGE_HUB_TAB = 'hub';
const UPLOAD_DOCUMENT_TAB = 'upload';
/* eslint-enable i18next/no-literal-string */

/**
 * Letting IQ suggest the audience needs nothing else, so the step is complete on the
 * choice alone. Picking your own is only complete once something is picked, and not
 * choosing at all leaves it incomplete rather than defaulting to either.
 */
function computeAudienceValid(state: AnalysisFormState): boolean {
  if (state.audienceMode === 'auto') return true;
  if (state.audienceMode === 'manual') {
    return state.audienceSource === 'upload'
      ? state.selectedExtractedIds.length > 0
      : state.selectedPersonaIds.length > 0;
  }
  return false;
}

interface AudienceStepProps {
  personas: PersonaDto[];
  isPending: boolean;
  onValidChange?: (isValid: boolean) => void;
}

export const AudienceStep = ({ personas, isPending, onValidChange }: AudienceStepProps) => {
  const { t } = useTranslation('analysis');
  const audienceMode = useAnalysisFormStore((s) => s.audienceMode);
  const setAudienceMode = useAnalysisFormStore((s) => s.setAudienceMode);
  const audienceSource = useAnalysisFormStore((s) => s.audienceSource);
  const setAudienceSource = useAnalysisFormStore((s) => s.setAudienceSource);
  const selectedPersonaIds = useAnalysisFormStore((s) => s.selectedPersonaIds);
  const extractedAudiences = useAnalysisFormStore((s) => s.extractedAudiences);
  // Selected as the boolean rather than the run, so the step only re-renders when the reading
  // starts or stops, not on every poll that writes to it.
  const reading = useAnalysisFormStore((s) => isExtractionInFlight(s.extraction));

  // Asked about before the other tab's work is dropped, and only when there is something to
  // drop. A report takes one source, so the switch is destructive by design - silently is what
  // it must not be.
  const [pendingSource, setPendingSource] = useState<AudienceSource | null>(null);

  useAnalysisStepValidity(computeAudienceValid, onValidChange);

  // A read still running counts: it produces no audiences until it finishes, so going by the
  // list alone would let the switch throw away the minutes someone is already waiting on
  // without asking.
  const hasWorkToLose =
    audienceSource === 'hub'
      ? selectedPersonaIds.length > 0
      : extractedAudiences.length > 0 || reading;

  const requestSource = (next: string) => {
    const source = next as AudienceSource;
    if (source === audienceSource) return;

    if (hasWorkToLose) {
      setPendingSource(source);
      return;
    }
    setAudienceSource(source);
  };

  return (
    <ModeChoiceStep
      title={t('analysis.audienceStepV2.audience')}
      subtitle={t('analysis.audienceStepV2.whoThisReportShouldFocus')}
      autoLabel={t('analysis.audienceStepV2.letIqSuggestMyAudience')}
      autoDescription={t('analysis.audienceStepV2.iqRunsDeepResearch')}
      manualLabel={t('analysis.audienceStepV2.defineMyOwnAudience')}
      manualDescription={t('analysis.audienceStepV2.handPickAudiencesFromThe')}
      value={audienceMode}
      onChange={setAudienceMode}
    >
      {audienceMode === 'manual' && (
        <Tabs value={audienceSource} onValueChange={requestSource}>
          <UnderlineTabsList>
            <UnderlineTabsTrigger value={KNOWLEDGE_HUB_TAB}>
              <BookOpenIcon className="size-md" />
              {t('analysis.audienceStepV2.fromKnowledgeHub')}
            </UnderlineTabsTrigger>
            <UnderlineTabsTrigger value={UPLOAD_DOCUMENT_TAB}>
              <FileUpIcon className="size-md" />
              {t('analysis.audienceStepV2.uploadDocument')}
            </UnderlineTabsTrigger>
          </UnderlineTabsList>
          <TabsContent value={KNOWLEDGE_HUB_TAB}>
            <KnowledgeHubAudiencePicker personas={personas} isPending={isPending} />
          </TabsContent>
          <TabsContent value={UPLOAD_DOCUMENT_TAB}>
            <UploadDocumentTab />
          </TabsContent>
        </Tabs>
      )}

      <AlertDialog open={pendingSource !== null} onOpenChange={() => setPendingSource(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('analysis.audienceStepV2.startFromTheOtherSource')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {audienceSource === 'hub'
                ? t('analysis.audienceStepV2.theAudiencesYouPicked')
                : reading
                  ? t('analysis.audienceStepV2.iqIsStillReading')
                  : t('analysis.audienceStepV2.theAudiencesReadFrom')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('analysis.audienceStepV2.keepEditing')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (pendingSource) setAudienceSource(pendingSource);
                setPendingSource(null);
              }}
            >
              {t('analysis.audienceStepV2.switchAndClear')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </ModeChoiceStep>
  );
};
